import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ListingCard from '../components/ListingCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Eye, MessageSquare, Car, Pencil, Trash2, User, Phone, MapPin, Save, ShieldCheck, Clock, CheckCircle, AlertTriangle, CreditCard, Loader2, Heart, Edit, Tag, Gavel, XCircle, Crown, Infinity, ChevronRight } from 'lucide-react';

const LOCATIONS = ['Accra', 'Kumasi', 'Tamale', 'Cape Coast', 'Takoradi', 'Tema', 'Ho', 'Sunyani', 'Bolgatanga', 'Koforidua'];

const ESCROW_STATUS_CONFIG = {
  pending: { label: 'Awaiting Payment', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  payment_received: { label: 'In Escrow', color: 'bg-blue-500/20 text-blue-400', icon: ShieldCheck },
  completed: { label: 'Completed', color: 'bg-trust-green/20 text-trust-green', icon: CheckCircle },
  disputed: { label: 'Disputed', color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
  failed: { label: 'Failed', color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
};

const DISPUTE_STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-yellow-500/20 text-yellow-400' },
  under_review: { label: 'Under Review', color: 'bg-blue-500/20 text-blue-400' },
  awaiting_response: { label: 'Awaiting Response', color: 'bg-orange-500/20 text-orange-400' },
  resolved: { label: 'Resolved', color: 'bg-green-500/20 text-green-400' },
  escalated: { label: 'Escalated', color: 'bg-purple-500/20 text-purple-400' },
  closed: { label: 'Closed', color: 'bg-zinc-500/20 text-zinc-400' },
};

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({ active_listings: 0, total_views: 0, conversations: 0, saved_count: 0 });
  const [listings, setListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [escrowTransactions, setEscrowTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: '', phone: '', location: '' });
  const [saving, setSaving] = useState(false);
  const [confirmingDelivery, setConfirmingDelivery] = useState(null);
  const [activeTab, setActiveTab] = useState('listings');
  const [showMarkSoldModal, setShowMarkSoldModal] = useState(null);
  const [markingSold, setMarkingSold] = useState(false);
  const [saleDetails, setSaleDetails] = useState({ buyer_name: '', sale_price: '', notes: '' });
  const [showCreateDisputeModal, setShowCreateDisputeModal] = useState(null);
  const [creatingDispute, setCreatingDispute] = useState(false);
  const [disputeForm, setDisputeForm] = useState({ dispute_type: '', description: '', desired_resolution: '' });

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || '', phone: user.phone || '', location: user.location || 'Accra' });
    
    // Check for escrow callback
    if (searchParams.get('escrow_callback')) {
      setActiveTab('escrow');
      toast.info('Payment initiated. Check your transactions below.');
    }
    
    Promise.all([
      API.get('/dashboard/stats'),
      API.get('/listings', { params: { page: 1, limit: 50 } }),
      API.get('/favorites'),
      API.get('/escrow/transactions'),
      API.get('/disputes/my-disputes'),
    ]).then(([s, l, f, e, d]) => {
      // Safely extract favorites array (handle various response formats)
      const favoritesData = Array.isArray(f.data) ? f.data : (f.data?.favorites || []);
      const escrowData = Array.isArray(e.data) ? e.data : (e.data?.transactions || []);
      const disputesData = Array.isArray(d.data?.disputes) ? d.data.disputes : (Array.isArray(d.data) ? d.data : []);
      const listingsData = l.data?.listings || [];
      
      setStats({ ...s.data, saved_count: favoritesData.length });
      setListings(listingsData.filter(x => x.seller_id === user.user_id));
      setFavorites(favoritesData);
      setEscrowTransactions(escrowData);
      setDisputes(disputesData);
      setLoading(false);
    }).catch((err) => {
      console.error('Dashboard data fetch error:', err);
      setLoading(false);
    });
  }, [user, searchParams]);

  const deleteListing = async (id) => {
    try {
      await API.delete(`/listings/${id}`);
      setListings(ls => ls.filter(l => l.listing_id !== id));
      toast.success('Listing deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const markAsSold = async (listingId) => {
    setMarkingSold(true);
    try {
      await API.post(`/listings/${listingId}/mark-sold`, {
        buyer_name: saleDetails.buyer_name || null,
        sale_price: saleDetails.sale_price ? parseFloat(saleDetails.sale_price) : null,
        notes: saleDetails.notes || null
      });
      setListings(ls => ls.map(l => l.listing_id === listingId ? { ...l, status: 'sold' } : l));
      toast.success('Listing marked as sold!');
      setShowMarkSoldModal(null);
      setSaleDetails({ buyer_name: '', sale_price: '', notes: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as sold');
    }
    setMarkingSold(false);
  };

  const createDispute = async (transactionId) => {
    if (!disputeForm.dispute_type || !disputeForm.description || !disputeForm.desired_resolution) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreatingDispute(true);
    try {
      const res = await API.post('/disputes/create', {
        transaction_id: transactionId,
        ...disputeForm
      });
      setDisputes(d => [res.data.dispute, ...d]);
      toast.success('Dispute created. AI is analyzing your case.');
      setShowCreateDisputeModal(null);
      setDisputeForm({ dispute_type: '', description: '', desired_resolution: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create dispute');
    }
    setCreatingDispute(false);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await API.put('/users/profile', profile);
      setUser(res.data);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const confirmDelivery = async (transactionId) => {
    setConfirmingDelivery(transactionId);
    try {
      await API.post('/escrow/confirm-delivery', {
        transaction_id: transactionId,
        confirmation_note: 'Vehicle received in good condition'
      });
      toast.success('Delivery confirmed! Funds will be released to the seller.');
      // Refresh transactions
      const e = await API.get('/escrow/transactions');
      setEscrowTransactions(e.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm delivery');
    }
    setConfirmingDelivery(null);
  };

  const verifyPayment = async (txRef) => {
    try {
      const res = await API.post(`/escrow/verify/${txRef}`);
      if (res.data.verified) {
        toast.success(res.data.message);
        const e = await API.get('/escrow/transactions');
        setEscrowTransactions(e.data || []);
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to verify payment');
    }
  };

  const formatPrice = (p) => new Intl.NumberFormat('en-GH').format(p);

  // Show loading state while user data is being fetched
  if (!user) {
    return (
      <main className="min-h-screen bg-background">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-gold mx-auto" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </main>
    );
  }

  // Show loading state while fetching dashboard data
  if (loading) {
    return (
      <main className="min-h-screen bg-background">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <Loader2 className="w-10 h-10 animate-spin text-gold mx-auto" />
            <p className="text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="dashboard-page" className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.name?.split(' ')[0]}</p>
          </div>
          <Link to="/create-listing">
            <Button className="bg-gold text-black hover:brightness-110 font-semibold gap-2" data-testid="dashboard-sell-btn">
              <Plus className="w-4 h-4" /> New Listing
            </Button>
          </Link>
        </div>

        {/* Stats - Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 md:grid-rows-2 gap-4 mb-8" data-testid="dashboard-stats">
          {/* Primary tile - spans 2x2 on md+ */}
          <div className="md:col-span-2 md:row-span-2 bg-gradient-to-br from-gold/15 via-card to-card border border-gold/30 rounded-2xl p-6 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-gold/10 blur-2xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-gold/20 flex items-center justify-center">
                  <Car className="w-5 h-5 text-gold" />
                </div>
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Active Listings</span>
              </div>
              <p className="font-mono-display text-5xl md:text-6xl font-bold leading-none tracking-tight">{stats.active_listings}</p>
              <p className="text-sm text-muted-foreground mt-2">Live on the marketplace right now</p>
            </div>
            <Link to="/create-listing" className="relative inline-flex items-center gap-2 text-sm text-gold hover:text-gold/80 mt-4 font-medium" data-testid="bento-create-listing">
              <Plus className="w-4 h-4" /> Add a new listing
            </Link>
          </div>

          {/* Secondary tiles */}
          {[
            { label: 'Total Views', value: stats.total_views, icon: Eye, accent: 'bg-blue-500/10 text-blue-400' },
            { label: 'Saved Cars', value: stats.saved_count, icon: Heart, accent: 'bg-rose-500/10 text-rose-400' },
            { label: 'Conversations', value: stats.conversations, icon: MessageSquare, accent: 'bg-emerald-500/10 text-emerald-400' },
            { label: 'In Escrow', value: escrowTransactions.length, icon: ShieldCheck, accent: 'bg-amber-500/10 text-amber-400' },
          ].map(({ label, value, icon: Icon, accent }) => (
            <div key={label} className="bg-card border border-border/50 rounded-xl p-5 hover:-translate-y-1 hover:border-gold/30 transition-[transform,border-color] duration-200">
              <div className={`w-9 h-9 rounded-xl ${accent} flex items-center justify-center mb-3`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="font-mono-display text-2xl font-bold leading-none">{value}</p>
              <p className="text-xs text-muted-foreground mt-1.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Early Bird Banner - for users with free year promo */}
        {user?.unlimited_listings_until && (
          <div className="bg-gradient-to-r from-trust-green/10 to-emerald-500/5 border border-trust-green/30 rounded-xl p-4 mb-8" data-testid="early-bird-banner">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-trust-green/20 flex items-center justify-center">
                <Crown className="w-6 h-6 text-trust-green" />
              </div>
              <div>
                <h3 className="font-semibold flex items-center gap-2 text-trust-green">
                  Early Bird Member
                  <Badge className="bg-trust-green text-white border-none text-[10px]">FREE YEAR</Badge>
                </h3>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Infinity className="w-4 h-4" /> Unlimited listings until {new Date(user.unlimited_listings_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dealer Upsell Banner - hide for early birds with unlimited */}
        {!user?.is_dealer && !user?.unlimited_listings_until && (
          <Link to="/dealer">
            <div className="bg-gradient-to-r from-gold/10 to-gold/5 border border-gold/30 rounded-xl p-4 mb-8 hover:border-gold/50 transition-colors group cursor-pointer" data-testid="dealer-upsell">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      Become a Dealer
                      <Badge className="bg-trust-green text-white border-none text-[10px]">7-Day Free Trial</Badge>
                    </h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Infinity className="w-4 h-4" /> Unlimited listings • From GHS 50/mo
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gold group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full max-w-2xl mb-6 overflow-x-auto">
            <TabsTrigger value="listings" className="flex-1" data-testid="tab-listings">My Listings</TabsTrigger>
            <TabsTrigger value="saved" className="flex-1" data-testid="tab-saved">Saved ({favorites.length})</TabsTrigger>
            <TabsTrigger value="escrow" className="flex-1" data-testid="tab-escrow">Escrow ({escrowTransactions.length})</TabsTrigger>
            <TabsTrigger value="disputes" className="flex-1" data-testid="tab-disputes">Disputes ({disputes.length})</TabsTrigger>
            <TabsTrigger value="profile" className="flex-1" data-testid="tab-profile">Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border/50 rounded-xl">
                <Car className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">No listings yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Start selling by creating your first listing.</p>
                <Link to="/create-listing"><Button className="bg-gold text-black hover:brightness-110 gap-2"><Plus className="w-4 h-4" /> Create Listing</Button></Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-listings-grid">
                {Array.isArray(listings) && listings.map(l => (
                  <div key={l.listing_id} className="relative group">
                    <ListingCard listing={l} showFavorite={false} />
                    {/* Sold Badge */}
                    {l.status === 'sold' && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl">
                        <div className="bg-red-600 text-white px-6 py-3 rounded-lg transform -rotate-12">
                          <Tag className="w-6 h-6 mx-auto mb-1" />
                          <span className="text-lg font-bold">SOLD</span>
                        </div>
                      </div>
                    )}
                    {/* Action Buttons */}
                    <div className="absolute top-3 left-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      {l.status !== 'sold' && (
                        <>
                          <Link to={`/edit-listing/${l.listing_id}`} onClick={(e) => e.stopPropagation()}>
                            <button className="w-8 h-8 rounded-full bg-gold/80 flex items-center justify-center text-black hover:bg-gold" data-testid={`edit-listing-${l.listing_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                          </Link>
                          <button onClick={(e) => { e.preventDefault(); setShowMarkSoldModal(l); }}
                            className="w-8 h-8 rounded-full bg-green-500/80 flex items-center justify-center text-white hover:bg-green-500" data-testid={`mark-sold-${l.listing_id}`} title="Mark as Sold">
                            <Tag className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <button onClick={(e) => { e.preventDefault(); deleteListing(l.listing_id); }}
                        className="w-8 h-8 rounded-full bg-red-500/80 flex items-center justify-center text-white hover:bg-red-500" data-testid={`delete-listing-${l.listing_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="saved">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border/50 rounded-xl">
                <Heart className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">No saved cars yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Browse listings and tap the heart to save them here.</p>
                <Link to="/browse"><Button className="bg-gold text-black hover:brightness-110 gap-2"><Car className="w-4 h-4" /> Browse Cars</Button></Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="saved-listings-grid">
                {Array.isArray(favorites) && favorites.map(fav => fav.listing && (
                  <ListingCard key={fav.favorite_id} listing={fav.listing} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="escrow">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : escrowTransactions.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border/50 rounded-xl">
                <ShieldCheck className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">No escrow transactions</h3>
                <p className="text-sm text-muted-foreground mb-4">Buy or sell vehicles with secure escrow payments.</p>
                <Link to="/browse"><Button className="bg-gold text-black hover:brightness-110 gap-2"><Car className="w-4 h-4" /> Browse Vehicles</Button></Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="escrow-transactions">
                {Array.isArray(escrowTransactions) && escrowTransactions.map(tx => {
                  const statusConfig = ESCROW_STATUS_CONFIG[tx.status] || ESCROW_STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  const isBuyer = tx.buyer_id === user?.user_id;
                  
                  // Fee breakdown from transaction
                  const fees = tx.fees || {};
                  const carPrice = fees.car_price || tx.amount;
                  const buyerFee = fees.buyer_fee || 0;
                  const sellerFee = fees.seller_fee || 0;
                  const totalBuyerPays = tx.amount_charged || (carPrice + buyerFee);
                  const sellerReceives = tx.seller_receives || (carPrice - sellerFee);
                  
                  return (
                    <div key={tx.transaction_id} className="bg-card border border-border/50 rounded-xl p-5" data-testid={`escrow-${tx.transaction_id}`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={`${statusConfig.color} border-none gap-1.5`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {isBuyer ? 'You are buying' : 'You are selling'}
                            </span>
                          </div>
                          
                          <h3 className="font-semibold mb-1">{tx.listing_title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {tx.vehicle_info?.make} {tx.vehicle_info?.model} {tx.vehicle_info?.year}
                          </p>
                          
                          {/* Fee Breakdown */}
                          <div className="mt-3 p-3 bg-muted/30 rounded-lg space-y-1.5">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Vehicle Price</span>
                              <span className="font-mono-display">GHS {formatPrice(carPrice)}</span>
                            </div>
                            {isBuyer ? (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Platform Fee (1.5%)</span>
                                  <span className="font-mono-display text-yellow-500">+GHS {formatPrice(buyerFee)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-semibold border-t border-border/50 pt-1.5 mt-1.5">
                                  <span>You Pay</span>
                                  <span className="font-mono-display text-gold">GHS {formatPrice(totalBuyerPays)}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Platform Fee (1.5%)</span>
                                  <span className="font-mono-display text-yellow-500">-GHS {formatPrice(sellerFee)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-semibold border-t border-border/50 pt-1.5 mt-1.5">
                                  <span>You Receive</span>
                                  <span className="font-mono-display text-trust-green">GHS {formatPrice(sellerReceives)}</span>
                                </div>
                              </>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <span className="text-muted-foreground">
                              {new Date(tx.created_at).toLocaleDateString('en-GH', { 
                                year: 'numeric', month: 'short', day: 'numeric' 
                              })}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col gap-2">
                          {/* Pending - Show payment link */}
                          {tx.status === 'pending' && isBuyer && tx.payment_link && (
                            <a href={tx.payment_link} target="_blank" rel="noopener noreferrer">
                              <Button className="bg-gold text-black hover:brightness-110 gap-2 w-full">
                                <CreditCard className="w-4 h-4" /> Complete Payment
                              </Button>
                            </a>
                          )}
                          
                          {/* Pending - Verify payment */}
                          {tx.status === 'pending' && isBuyer && (
                            <Button variant="outline" size="sm" onClick={() => verifyPayment(tx.tx_ref)}>
                              Check Payment Status
                            </Button>
                          )}
                          
                          {/* Payment Received - Buyer can confirm delivery */}
                          {tx.status === 'payment_received' && isBuyer && (
                            <Button 
                              className="bg-trust-green hover:bg-trust-green/90 text-white gap-2"
                              onClick={() => confirmDelivery(tx.transaction_id)}
                              disabled={confirmingDelivery === tx.transaction_id}
                            >
                              {confirmingDelivery === tx.transaction_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                              Confirm Delivery
                            </Button>
                          )}
                          
                          {/* Payment Received - Seller sees status */}
                          {tx.status === 'payment_received' && !isBuyer && (
                            <div className="text-sm text-muted-foreground bg-blue-500/10 px-3 py-2 rounded-lg">
                              <ShieldCheck className="w-4 h-4 inline mr-1 text-blue-400" />
                              Funds held securely. Awaiting buyer confirmation.
                            </div>
                          )}
                          
                          {/* Completed */}
                          {tx.status === 'completed' && (
                            <div className="text-sm text-trust-green bg-trust-green/10 px-3 py-2 rounded-lg">
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              Transaction complete!
                            </div>
                          )}
                          
                          {/* Link to listing */}
                          <Link to={`/listing/${tx.listing_id}`}>
                            <Button variant="ghost" size="sm" className="w-full">
                              View Listing
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile">
            <div className="bg-card border border-border/50 rounded-xl p-6 max-w-lg space-y-5" data-testid="profile-form">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={profile.name} onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                    className="pl-10 h-12" data-testid="profile-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={profile.phone} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                    className="pl-10 h-12" data-testid="profile-phone" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Select value={profile.location} onValueChange={(v) => setProfile(p => ({ ...p, location: v }))}>
                  <SelectTrigger className="h-12" data-testid="profile-location"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={saveProfile} disabled={saving} className="bg-gold text-black hover:brightness-110 gap-2 h-12" data-testid="save-profile-btn">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : disputes.length === 0 ? (
              <div className="text-center py-16 bg-card border border-border/50 rounded-xl">
                <Gavel className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">No disputes</h3>
                <p className="text-sm text-muted-foreground">You haven't filed any disputes yet. If you have an issue with a transaction, you can create a dispute from the Escrow tab.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Array.isArray(disputes) && disputes.map(d => (
                  <div key={d.dispute_id} className="bg-card border border-border/50 rounded-xl p-5" data-testid={`dispute-${d.dispute_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold">{d.listing_title}</h4>
                        <p className="text-sm text-muted-foreground">Dispute ID: {d.dispute_id}</p>
                      </div>
                      <Badge className={DISPUTE_STATUS_CONFIG[d.status]?.color || 'bg-zinc-500/20'}>
                        {DISPUTE_STATUS_CONFIG[d.status]?.label || d.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <span className="text-muted-foreground">Type:</span>
                        <span className="ml-2 capitalize">{d.dispute_type?.replace(/_/g, ' ')}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="ml-2 text-gold font-semibold">GH₵{d.amount?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Created:</span>
                        <span className="ml-2">{new Date(d.created_at).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Role:</span>
                        <span className="ml-2 capitalize">{d.initiated_by === 'buyer' ? (d.buyer_id === user?.user_id ? 'You (Buyer)' : 'Buyer') : (d.seller_id === user?.user_id ? 'You (Seller)' : 'Seller')}</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{d.description}</p>
                    
                    {/* AI Analysis */}
                    {d.ai_analysis && (
                      <div className="bg-background rounded-lg p-4 mb-3">
                        <h5 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <ShieldCheck className="w-4 h-4 text-gold" /> AI Analysis
                        </h5>
                        <p className="text-sm text-muted-foreground">{d.ai_analysis.reasoning}</p>
                        <div className="mt-2 flex gap-2">
                          <Badge variant="outline" className="capitalize">
                            Recommendation: {d.ai_analysis.recommendation?.replace(/_/g, ' ')}
                          </Badge>
                          <Badge variant="outline">
                            Confidence: {d.ai_analysis.confidence}
                          </Badge>
                        </div>
                      </div>
                    )}
                    
                    {/* Resolution */}
                    {d.resolution && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-green-400 mb-1">Resolution</h5>
                        <p className="text-sm">{d.resolution.type?.replace(/_/g, ' ')}</p>
                        {d.resolution.admin_notes && <p className="text-xs text-muted-foreground mt-1">{d.resolution.admin_notes}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Mark as Sold Modal */}
        {showMarkSoldModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-green-500" />
                Mark as Sold
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Congratulations on selling your {showMarkSoldModal.make} {showMarkSoldModal.model}! Fill in the optional details below.
              </p>
              <div className="space-y-4">
                <div>
                  <Label>Buyer's Name (optional)</Label>
                  <Input
                    placeholder="John Doe"
                    value={saleDetails.buyer_name}
                    onChange={(e) => setSaleDetails(s => ({ ...s, buyer_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Final Sale Price (optional)</Label>
                  <Input
                    type="number"
                    placeholder={showMarkSoldModal.price?.toString()}
                    value={saleDetails.sale_price}
                    onChange={(e) => setSaleDetails(s => ({ ...s, sale_price: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea
                    placeholder="Any additional notes about the sale..."
                    value={saleDetails.notes}
                    onChange={(e) => setSaleDetails(s => ({ ...s, notes: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowMarkSoldModal(null)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={() => markAsSold(showMarkSoldModal.listing_id)}
                  disabled={markingSold}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                >
                  {markingSold ? 'Marking...' : 'Mark as Sold'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Dispute Modal */}
        {showCreateDisputeModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-orange-500" />
                Create Dispute
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our AI will analyze your dispute and provide a fair resolution recommendation.
              </p>
              <div className="space-y-4">
                <div>
                  <Label>Dispute Type *</Label>
                  <Select value={disputeForm.dispute_type} onValueChange={(v) => setDisputeForm(f => ({ ...f, dispute_type: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="delivery_issue">Delivery Issue</SelectItem>
                      <SelectItem value="item_not_as_described">Item Not As Described</SelectItem>
                      <SelectItem value="payment_issue">Payment Issue</SelectItem>
                      <SelectItem value="seller_unresponsive">Seller Unresponsive</SelectItem>
                      <SelectItem value="buyer_unresponsive">Buyer Unresponsive</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    placeholder="Describe the issue in detail..."
                    value={disputeForm.description}
                    onChange={(e) => setDisputeForm(f => ({ ...f, description: e.target.value }))}
                    rows={4}
                  />
                </div>
                <div>
                  <Label>Desired Resolution *</Label>
                  <Select value={disputeForm.desired_resolution} onValueChange={(v) => setDisputeForm(f => ({ ...f, desired_resolution: v }))}>
                    <SelectTrigger><SelectValue placeholder="What outcome do you want?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_refund">Full Refund</SelectItem>
                      <SelectItem value="partial_refund">Partial Refund</SelectItem>
                      <SelectItem value="complete_transaction">Complete Transaction</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowCreateDisputeModal(null)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={() => createDispute(showCreateDisputeModal)}
                  disabled={creatingDispute}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {creatingDispute ? 'Creating...' : 'Submit Dispute'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
